import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import {
  faBuilding,
  faCouch,
  faGraduationCap,
  faStethoscope,
} from '@fortawesome/pro-light-svg-icons';
import Container from '@v2/shared/components/Container';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { cn } from '@v2/shared/lib/utils';

/*
 * Shown on every bulk-order page: a visitor who lands on the wrong variant can still see
 * the other settings we supply and jump to the right one. Each card deep-links into its
 * variant — the `slug` is appended to `/bulk-order/` here so it stays right for whichever
 * site is serving.
 *
 * Icons are typed IconProp rather than IconDefinition: each pro-* package ships its own
 * nested copy of fontawesome-common-types, so IconDefinition is a different type identity
 * depending on which package the icon came from.
 */
const cards: { id: number; icon: IconProp; title: string; description: string; slug: string }[] = [
  {
    id: 1,
    icon: faCouch as IconProp,
    title: 'Medical & Waiting Rooms',
    description: 'Make waiting time fly for patients. Auto-renewals keep your lobby fresh.',
    slug: 'waiting-rooms',
  },
  {
    id: 2,
    icon: faStethoscope as IconProp,
    title: 'Doctors & Medical Offices',
    description: 'Patient-friendly mixes for every specialty, from pediatrics to hospitals.',
    slug: 'medical-offices',
  },
  {
    id: 3,
    icon: faGraduationCap as IconProp,
    title: 'Schools & Libraries',
    description: 'POs accepted. Trusted by districts & universities. Nat Geo, Time & more.',
    slug: 'school-libraries',
  },
  {
    id: 4,
    icon: faBuilding as IconProp,
    title: 'Corporate Offices & Gifts',
    description: 'Break rooms, lobbies, and client gifts with your name on every cover.',
    slug: 'corporate-offices',
  },
];

export default function WhoWeSupply() {
  const { buildUrl } = useUrlGenerator();

  return (
    <section className="bg-slate-100 py-10 md:py-20">
      <Container>
        <div className="text-center mb-8">
          <h2 className="font-bold text-3xl lg:text-4xl mb-2">Who We Supply</h2>
          <p className="text-slate-600 text-lg">
            Pick your setting for tailored recommendations and titles.
          </p>
        </div>
        <div
          className={cn(
            'grid grid-cols-1 gap-4',
            'md:grid-cols-2 md:gap-6',
            'lg:grid-cols-4 lg:gap-10'
          )}
        >
          {cards.map(({ id, title, icon, description, slug }) => (
            <a
              key={id}
              href={buildUrl(`/bulk-order/${slug}`)}
              className={cn(
                'bg-white rounded-xl p-6 lg:p-10 flex flex-col items-center justify-start text-center',
                'no-underline text-slate-800 border-2 border-solid border-transparent',
                'transition hover:border-red-600 hover:shadow-lg'
              )}
            >
              <FontAwesomeIcon icon={icon} className="text-5xl mb-5 text-red-600" />
              <h3 className="text-xl font-semibold mb-3">{title}</h3>
              <p className="text-slate-700 mb-4">{description}</p>
              <span className="mt-auto text-red-600 font-semibold">
                Explore <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
              </span>
            </a>
          ))}
        </div>
      </Container>
    </section>
  );
}
